import React, { CSSProperties } from 'react';
import { findDOMNode } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { View } from '@shared/components/View';
import { MBSentry } from '@shared/util/sentry';
import { MBOverlayOptions } from './types';

/*
    This helper class makes it possible to overlay any React component
    anywhere in the screen from web. Basically a new React DOM is created
    inside div#shared-components-tooltip
*/
export const MBOverlay = {
  root() {
    const container = getOrCreateContainer('shared-components-tooltip');
    return createRoot(container);
  },
  show({
    parent,
    closeOnBackdropPress,
    onClose,
    onBackdropPress,
    position = defaultPosition,
    component: Component,
  }: MBOverlayOptions) {
    // 1. Get the HTML node to render the action sheet in. This element is
    //    defined in public/index.html.ejs
    const root = MBOverlay.root();

    // 2. Get the parent's DOM node and calculate it's absolute position in the screen
    // Note: we need to take the current window's scroll position into account
    // (especially for Storybook): https://stackoverflow.com/a/27129257/9117995
    const parentNode = (() => {
      try {
        // eslint-disable-next-line react/no-find-dom-node
        return findDOMNode(parent) as Element;
      } catch (err) {
        // ReactDOM.findDOMNode causes the following error in Sentry:
        // https://sentry.io/share/issue/b13523def7ba4db1accbae1bd70fb766
        // We do not
        const error = err instanceof Error ? err : new Error(String(err));
        console.warn('[MBOverlay] failed to get parent DOM node', error.message);
        MBSentry.exception({ error });
      }
    })();
    if (!parentNode) {
      return;
    }
    const parentNodeRect = parentNode.getBoundingClientRect();
    const parentNodeRectWithScrollOffset: DOMRect = {
      top: parentNodeRect.top + window.scrollY,
      left: parentNodeRect.left + window.scrollX,
      right: parentNodeRect.right - window.scrollX,
      bottom: parentNodeRect.bottom - window.scrollY,
      width: parentNodeRect.width,
      height: parentNodeRect.height,
    } as DOMRect;

    // 3. Get the style for the content to be overlaid (read more docs above the function)
    const positionStyle = position(parentNodeRectWithScrollOffset);

    // Close the action sheet by simply removing the React component mounted in the container
    // TODO(fant): make the component fade out so that's not so disruptive
    function close() {
      root.unmount();
      onClose?.();
    }

    function handleBackdropPress() {
      close();
      onBackdropPress?.();
    }

    const Backdrop = () => {
      const [visible, setVisible] = React.useState(false);
      // Why set timeout? Otherwise, if closeOnBackdropPress is set, we will
      // render "backdrop". When pressed it will close the overlay. On mobile
      // web, backdrop's div's onClick is triggered from the click that made
      // the MBOverlay.show call, which makes the overlay disappear right away.
      // By showing the overlay in the next run loop, we avoid this behavior.
      React.useEffect(() => {
        const timer = setTimeout(() => setVisible(true), 500);
        return () => clearTimeout(timer);
      }, []);
      if (!visible) {
        return null;
      }

      return (
        <div
          onClick={handleBackdropPress}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
      );
    };

    // Make sure this zIndex is higher than webReactModalOverlay.zIndex
    root.render(
      <View absolute style={{ zIndex: 10000 }}>
        {!!closeOnBackdropPress && <Backdrop />}
        <View relative style={positionStyle}>
          <Backdrop />
          <Component close={close} />
        </View>
      </View>,
    );
  },

  /*

    */
  hide() {
    const root = MBOverlay.root();
    root.unmount();
  },
};

/*
    Get the container we will place the tooltip in. This should be declared
    in index.html. However, if it doesn't exist (eg in Storybook), we create
    the container element-

    Inspired by: https://stackoverflow.com/a/26083136/9117995
*/
function getOrCreateContainer(id: string) {
  const container = document.getElementById(id);
  if (!!container) {
    return container;
  }

  const newContainer = document.createElement('div');
  newContainer.setAttribute('id', id);
  document.body.appendChild(newContainer);
  return newContainer;
}

function defaultPosition({ top, left, right, bottom }: DOMRect): CSSProperties {
  return {
    top,
    left,
    position: 'absolute',
    right: window.innerWidth - right,
    bottom: window.innerHeight - bottom,
  };
}
